// ai-sidebar/components/result-card.tsx
// The closed-loop artifact for the completed-action state (Direction D,
// frameCompleted): a created deliverable with Save / Open / Refine actions.
import { useState } from "react";
import { FileText, CheckCircle2, Bookmark, BookmarkCheck, ExternalLink, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAssistant } from "../use-assistant";
import { stripEmDashes } from "./markdown";
import { saveInsight } from "@/app/services/reporting/insights-api-service";

export interface ResultCardData {
  title: string;
  subtitle?: string;
  preview?: string;
}

export function ResultCard({ data: rawData }: { data: ResultCardData }) {
  const { brandName, currentPage } = useAssistant();
  const [saved, setSaved] = useState(false);
  // House rule: never surface an em dash, including in model-authored cards.
  const data: ResultCardData = {
    title: stripEmDashes(rawData.title),
    subtitle: rawData.subtitle ? stripEmDashes(rawData.subtitle) : rawData.subtitle,
    preview: rawData.preview ? stripEmDashes(rawData.preview) : rawData.preview,
  };

  const save = async () => {
    if (saved) return;
    const brand = brandName || (typeof localStorage !== "undefined" ? localStorage.getItem("bm_brand") : null);
    if (!brand) { toast.error("Select a brand first to save insights."); return; }
    try {
      await saveInsight({
        brand_name: brand,
        module: "assistant",
        title: data.title.slice(0, 120),
        subtitle: (data.preview || data.subtitle || "").slice(0, 200),
        saved_from: "Page Assistant",
        raw_data: { kind: "result_card", ...data, page: currentPage },
      });
      setSaved(true);
      toast.success("Saved to Insights", { description: data.title });
    } catch (e: any) {
      toast.error(e?.message || "Could not save to insights.");
    }
  };

  return (
    <div className="overflow-hidden" style={{ border: "1px solid var(--border)", borderRadius: 13, boxShadow: "var(--elevation-sm)" }}>
      <div className="flex items-center gap-2.5" style={{ padding: "11px 13px", borderBottom: "1px solid var(--border)", background: "var(--muted)" }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 26, height: 26, borderRadius: 7, background: "var(--spectral-violet-10)" }}>
          <FileText style={{ width: 14, height: 14, strokeWidth: 1.85, color: "var(--spectral-violet)" }} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-foreground truncate" style={{ fontSize: 12.5, fontWeight: 600 }}>{data.title}</div>
          {data.subtitle && <div className="text-muted-foreground truncate" style={{ fontSize: 11 }}>{data.subtitle}</div>}
        </div>
        <span className="inline-flex items-center gap-1 shrink-0" style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ai-teal-text)" }}>
          <CheckCircle2 style={{ width: 13, height: 13, strokeWidth: 1.85, color: "var(--ai-teal-text)" }} />
          Created
        </span>
      </div>

      <div className="text-muted-foreground" style={{ padding: "12px 13px", fontSize: 12, lineHeight: 1.55 }}>
        {data.preview || (
          <div className="space-y-1.5">
            <div style={{ height: 7, width: "92%", borderRadius: 4, background: "var(--muted)" }} />
            <div style={{ height: 7, width: "100%", borderRadius: 4, background: "var(--muted)" }} />
            <div style={{ height: 7, width: "78%", borderRadius: 4, background: "var(--muted)" }} />
          </div>
        )}
      </div>

      <div className="flex" style={{ gap: 7, padding: "0 13px 13px" }}>
        <button
          type="button"
          onClick={save}
          className="flex-1 inline-flex items-center justify-center gap-1.5"
          style={{ padding: 8, borderRadius: 8, border: "1px solid var(--spectral-violet-20)", background: "var(--spectral-violet-10)", color: "var(--spectral-violet)", fontSize: 12, fontWeight: 600 }}
        >
          {saved ? <BookmarkCheck style={{ width: 13, height: 13, strokeWidth: 1.85 }} /> : <Bookmark style={{ width: 13, height: 13, strokeWidth: 1.85 }} />}
          {saved ? "Saved" : "Save to insights"}
        </button>
        <button
          type="button"
          onClick={() => toast("Opening in Reports is coming soon.")}
          className="inline-flex items-center gap-1.5 text-foreground bg-card"
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", fontSize: 12, fontWeight: 600 }}
        >
          <ExternalLink style={{ width: 13, height: 13, strokeWidth: 1.85, color: "var(--muted-foreground)" }} />
          Open
        </button>
        <button
          type="button"
          onClick={() => toast("Refine is coming soon.")}
          aria-label="Refine"
          className="inline-flex items-center justify-center bg-card text-muted-foreground"
          style={{ width: 34, borderRadius: 8, border: "1px solid var(--border)" }}
        >
          <Pencil style={{ width: 14, height: 14, strokeWidth: 1.85 }} />
        </button>
      </div>
    </div>
  );
}
